import { useEffect, memo, useMemo, useRef } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  FormHelperText,
  Input,
  Alert as MuiAlert,
} from '@mui/material'

import { useAppSelector } from 'redux/Hook'
import useValidation from 'components/Reporting/Overview/FilterBuilder/FilterSet/FilterDialogue/UseValidation'
import { CHAR_LENGTH } from 'components/Reporting/Overview/FilterBuilder/FilterSet/Constants'
import { isMobileView } from 'lib/GlobalConstants'

const SaveModalDataContent = ({
  setUserInput,
  t,
  savedSelectionFilters,
  savedFilter,
  originalFilterName,
  filterSetName,
}: any) => {
  const updatedFilterName =
    filterSetName === 'rename' ? originalFilterName : savedFilter
  const {
    handleChange,
    updateValue,
    textValue: { validation, text = '', isError, position = 'right' } = {},
    textValue,
  } = useValidation({ savedFilter: updatedFilterName })
  const ref: any = useRef('')
  const {
    features: {
      features: { overview_dashboard_sharing: { enabled = false } = {} } = {},
    } = {},
  }: any = useAppSelector((state) => state)

  // used to show error message right/left below input
  const myFormHelperText = (updatedText: string) => {
    let helperText = ''

    const textLength = updatedText?.length || 0
    if (validation) {
      helperText = validation
    } else {
      helperText = `${textLength}/${CHAR_LENGTH}`
    }

    return (
      <FormHelperText
        sx={{
          textAlign: position,
        }}
      >
        {helperText}
      </FormHelperText>
    )
  }

  //sepcically used to make two lines while user is typing
  const isMultilineView = useMemo(() => {
    const textLength = text?.length
    return isMobileView ? textLength > 30 : textLength > 60
  }, [text, updatedFilterName])

  useEffect(() => {
    setUserInput({
      updateValue,
      textValue,
    })
  }, [text])

  /** focus at the end when selected filter is present */
  useEffect(() => {
    if (updatedFilterName?.length) {
      ref?.current?.setSelectionRange(
        updatedFilterName?.length,
        updatedFilterName?.length,
      )
      ref?.current?.focus()
    }
  }, [updatedFilterName, ref?.current])

  /** focus at the end when selected filter is present */
  useEffect(() => {
    ref?.current?.focus()
  }, [])

  useEffect(() => {
    updatedFilterName?.length &&
      updateValue({
        isError: false,
        text: updatedFilterName,
        validation: '',
        position: 'right',
      })
  }, [updatedFilterName])

  const saveMessage = () => {
    const isTextChanged =
      updatedFilterName?.toLowerCase()?.trim() === text?.toLowerCase()?.trim()
    return savedSelectionFilters?.length ? (
      <>
        {isTextChanged ? (
          enabled ? (
            <Box sx={{ marginTop: 3 }}>
              <MuiAlert severity="info">
                {t('reporting.filterSet.shareNotification')}
              </MuiAlert>
            </Box>
          ) : null
        ) : (
          <Box sx={{ marginTop: 3 }}>
            <MuiAlert severity="info">
              <span
                dangerouslySetInnerHTML={{
                  __html: t('reporting.filterSet.saveAsFilterSet'),
                }}
              />
            </MuiAlert>
          </Box>
        )}
      </>
    ) : null
  }

  return (
    <>
      <div>
        {filterSetName !== 'rename' &&
          (enabled
            ? t('reporting.filterSet.combinationFilters')
            : t('reporting.filterSet.combinationFiltersHidden'))}
      </div>
      <Box
        component="form"
        sx={{
          paddingTop: '1.2rem',
        }}
        noValidate
        autoComplete="off"
        key={'saveContent'}
      >
        <FormControl
          data-testid="save-filterset-form"
          error={isError}
          variant="standard"
          sx={{ width: '100%' }}
        >
          <InputLabel data-testid="save-filterset-input-label">
            {t('reporting.filterSet.filterSetName')}
          </InputLabel>
          <Input
            autoFocus
            inputProps={{
              maxLength: CHAR_LENGTH,
            }}
            multiline
            rows={isMultilineView ? '2' : '1'}
            value={text}
            defaultValue={updatedFilterName}
            onChange={(e: any) => {
              handleChange(e)
            }}
            aria-describedby="component-error-text"
            data-testid="save-filterset-input"
            inputRef={ref}
            key="saveContentInput"
          />
          {myFormHelperText(text)}
        </FormControl>

        {saveMessage()}
      </Box>
    </>
  )
}

export default memo(SaveModalDataContent)
